import { useEffect, useState } from 'react';
import type { EmailTemplate } from '@epasu/core';
import { crm, type FollowupRow, type WaveStartDto } from '@/lib/crm-client';
import { startAbgewiesen, versanddauer } from '@/lib/campaign-schedule';
import { Button } from '@/components/ui/button';

interface Props {
  campaignId: string;
  campaignName: string;
  onClose: () => void;
  onSent: () => void;
}

interface Uebersicht {
  faellig: FollowupRow[];
  konto: { label: string; fromAddress: string } | null;
  limitLeft: number;
  pause: { min: number; max: number } | null;
  vorlagenJeStufe: { stufe: number; vorlage: string; anzahl: number }[];
  /** `sendDue` weist den Start ab, wenn die Kampagne nicht aktiv ist. */
  nichtAktiv: boolean;
}

/**
 * Zweistufig wie `WavePreviewDialog`: dieser Dialog zeigt erst, was
 * gesendet würde, und verschickt erst auf den zweiten Klick.
 *
 * Es gibt dafür keine eigene Vorschau-Methode im Vertrag — `campaigns.wavePreview`
 * ist für die Erstansprache gebaut. Konto und verbleibendes Tageslimit lassen
 * sich trotzdem von dort übernehmen: `sentToday` in `engine/wave.ts` zählt
 * Erstansprachen und Nachfassen zusammen, dasselbe Tageslimit gilt für beide.
 *
 * Wie die Welle läuft auch das Nachfassen mit Pausen im Hintergrund; der Klick
 * startet es nur.
 */
export function FollowupSendDueDialog({ campaignId, campaignName, onClose, onSent }: Props) {
  const [uebersicht, setUebersicht] = useState<Uebersicht | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const [sendeFehler, setSendeFehler] = useState<string | null>(null);
  const [gestartet, setGestartet] = useState<WaveStartDto | null>(null);

  useEffect(() => {
    let abgebrochen = false;
    void Promise.all([
      crm.followups.list({ campaignId, due: true }),
      crm.campaigns.get(campaignId),
      crm.campaigns.wavePreview(campaignId),
      crm.campaigns.steps(campaignId),
      crm.templates.list(),
      crm.mailAccounts.list(),
    ])
      .then(([faellig, kampagne, vorschau, steps, vorlagen, konten]) => {
        if (abgebrochen) return;
        const vorlagenName = new Map<string, string>(vorlagen.map((v: EmailTemplate) => [v.id, v.name]));
        const gruppiert = new Map<number, number>();
        for (const z of faellig) {
          if (z.nextStep !== null) gruppiert.set(z.nextStep, (gruppiert.get(z.nextStep) ?? 0) + 1);
        }
        const vorlagenJeStufe = [...gruppiert].map(([stufe, anzahl]) => {
          const schritt = steps.find((s) => s.stepNumber === stufe + 1);
          const vorlage = schritt?.templateId ? (vorlagenName.get(schritt.templateId) ?? 'unbekannte Vorlage') : 'keine Vorlage hinterlegt';
          return { stufe, vorlage, anzahl };
        });
        const konto = kampagne?.senderAccountId
          ? konten.find((k) => k.id === kampagne.senderAccountId)
          : undefined;
        setUebersicht({
          faellig,
          konto: konto ? { label: konto.label, fromAddress: konto.fromAddress } : null,
          limitLeft: vorschau.limitLeft,
          pause: kampagne ? { min: kampagne.sendPauseMinSec, max: kampagne.sendPauseMaxSec } : null,
          vorlagenJeStufe,
          nichtAktiv: kampagne?.status !== 'aktiv',
        });
      })
      .catch((err: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(err instanceof Error ? err.message : 'Übersicht konnte nicht geladen werden');
        }
      });
    return () => {
      abgebrochen = true;
    };
  }, [campaignId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const senden = async () => {
    setBusy(true);
    setSendeFehler(null);
    try {
      const res = await crm.followups.sendDue({ campaignId });
      if (res.started) {
        setGestartet(res);
        onSent();
      } else {
        setSendeFehler(res.reason ? startAbgewiesen(res.reason) : 'Das Nachfassen wurde nicht gestartet.');
      }
    } catch (err) {
      setSendeFehler(err instanceof Error ? err.message : 'Versand fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  // Mehr als das Tageslimit geht heute ohnehin nicht raus.
  const heute = uebersicht ? Math.min(uebersicht.faellig.length, uebersicht.limitLeft) : 0;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Fällige Follow-ups senden"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="my-auto w-full max-w-2xl rounded-lg border bg-background p-6 shadow-lg">
        <h2 className="text-lg font-semibold">Fällige Follow-ups — {campaignName}</h2>

        {ladeFehler && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {ladeFehler}
          </p>
        )}

        {!uebersicht && !ladeFehler ? (
          <p className="mt-6 text-sm text-muted-foreground">Lade Übersicht …</p>
        ) : uebersicht ? (
          gestartet ? (
            <div className="mt-4 space-y-3">
              <p className="text-sm">
                <strong>Das Nachfassen läuft.</strong> {gestartet.planned}{' '}
                {gestartet.planned === 1 ? 'Follow-up geht' : 'Follow-ups gehen'} nacheinander raus,{' '}
                {versanddauer(gestartet.planned, gestartet.pauseSec)}.
              </p>
              <p className="text-sm text-muted-foreground">
                Du kannst den Dialog schließen — der Versand läuft auf dem Server weiter. Das
                Ergebnis steht danach im Protokoll. Lehnt der Anbieter vorübergehend ab, hält das
                Nachfassen von selbst an; die übrigen bleiben fällig.
              </p>
              <div className="flex justify-end">
                <Button onClick={onClose}>Fertig</Button>
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <dl className="grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Anzahl
                  </dt>
                  <dd className="mt-0.5">{uebersicht.faellig.length} fällige Follow-ups</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Konto
                  </dt>
                  <dd className="mt-0.5">
                    {uebersicht.konto
                      ? `${uebersicht.konto.label} — ${uebersicht.konto.fromAddress}`
                      : 'Standardkonto'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Verbleibendes Tageslimit
                  </dt>
                  <dd className="mt-0.5">{uebersicht.limitLeft}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Vorlagen
                  </dt>
                  <dd className="mt-0.5">
                    {uebersicht.vorlagenJeStufe.length === 0
                      ? '—'
                      : uebersicht.vorlagenJeStufe
                          .map((v) => `${v.stufe}. Nachfassen — ${v.vorlage} (${v.anzahl})`)
                          .join(', ')}
                  </dd>
                </div>
                {uebersicht.pause && heute > 0 && (
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Dauer
                    </dt>
                    <dd className="mt-0.5">
                      {versanddauer(heute, uebersicht.pause)} — {uebersicht.pause.min}–
                      {uebersicht.pause.max} Sekunden Pause zwischen den Mails
                    </dd>
                  </div>
                )}
              </dl>

              {uebersicht.nichtAktiv && (
                <p className="rounded-md bg-secondary p-3 text-sm text-muted-foreground">
                  Diese Kampagne ist nicht aktiv — erst auf „Aktiv“ stellen, dann geht wirklich
                  etwas raus.
                </p>
              )}

              <div>
                <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Empfänger ({uebersicht.faellig.length})
                </h3>
                <div className="mt-2 max-h-48 overflow-y-auto rounded-md border">
                  <ul className="divide-y text-sm">
                    {uebersicht.faellig.map((z) => (
                      <li key={z.messageId} className="flex justify-between gap-3 px-3 py-1.5">
                        <span className="truncate">
                          {z.contactName ?? z.contactEmail ?? '—'}
                          {z.organizationName && ` · ${z.organizationName}`}
                        </span>
                        <span className="shrink-0 text-muted-foreground">
                          {z.nextStep === null ? '—' : `${z.nextStep}. Nachfassen`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {sendeFehler && (
                <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {sendeFehler}
                </p>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onClose} disabled={busy}>
                  Abbrechen
                </Button>
                <Button
                  onClick={() => void senden()}
                  disabled={busy || uebersicht.nichtAktiv || uebersicht.faellig.length === 0}
                >
                  {busy ? 'Starte …' : `${uebersicht.faellig.length} Follow-ups jetzt senden`}
                </Button>
              </div>
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}
